import { z } from 'zod';
import { OTHER_JOB_TITLE } from '@/features/auth/job-titles';
import { firstUnsatisfiedRule } from '@/features/auth/password-rules';

/** Longueur du code de confirmation d'email, alignée sur `EmailConfirmationCode` côté C#. */
export const CONFIRMATION_CODE_LENGTH = 4;

/**
 * Schémas de validation des formulaires.
 *
 * Ces règles DUPLIQUENT volontairement celles du serveur. Ce n'est pas une redondance
 * inutile : côté client elles donnent un retour immédiat sans aller-retour réseau, côté
 * serveur elles constituent la seule barrière réelle (un client peut toujours être
 * contourné). Les deux doivent rester alignées — les seuils sont donc repris à l'identique
 * de `PasswordPolicy` côté C#, et les tests d'intégration vérifient que les messages
 * serveur remontent correctement dans les formulaires.
 */

/**
 * Toutes les règles de `password-rules.ts` sont BLOQUANTES, y compris les motifs interdits.
 * C'est le même module qui alimente la liste à cocher affichée sous le champ : une règle
 * visiblement non remplie doit empêcher de passer à l'étape suivante, sans quoi l'échec est
 * simplement repoussé jusqu'au refus du serveur, à la toute fin du parcours.
 *
 * L'email n'est pas connu à ce niveau (il l'est au niveau de l'objet) : la règle qui en
 * dépend est donc évaluée avec une chaîne vide ici — donc neutre — et re-vérifiée par le
 * `refine` de `registerCredentialsSchema` ci-dessous, qui dispose des deux champs.
 */
const password = z.string().superRefine((value, ctx) => {
  const failed = firstUnsatisfiedRule(value, '');

  if (failed !== null) {
    ctx.addIssue({ code: 'custom', message: failed.error(value, '') });
  }
});

const email = z
  .string()
  .min(1, "L'adresse email est obligatoire.")
  .email("L'adresse email n'est pas au bon format.")
  .max(320);

const companyName = z
  .string()
  .min(1, 'La raison sociale est obligatoire.')
  .max(200, 'La raison sociale est limitée à 200 caractères.');

const firstName = z.string().min(1, 'Le prénom est obligatoire.').max(100);
const lastName = z.string().min(1, 'Le nom est obligatoire.').max(100);

export const loginSchema = z.object({
  email,

  // Aucune règle de longueur à la CONNEXION : les comptes créés sous une politique plus
  // ancienne doivent rester utilisables, et exiger 12 caractères ici renseignerait un
  // attaquant sur la forme des mots de passe acceptés.
  password: z.string().min(1, 'Le mot de passe est obligatoire.'),
});

/**
 * Inscription, première étape : identifiants.
 *
 * La confirmation du mot de passe n'existe QUE côté client — elle n'est pas envoyée à
 * l'API. Son rôle est d'attraper une faute de frappe sur un champ masqué, faute qui se
 * solderait sinon par un compte inaccessible et un parcours de réinitialisation.
 */
export const registerCredentialsSchema = z
  .object({
    email,
    password,
    confirmPassword: z.string().min(1, 'Veuillez confirmer votre mot de passe.'),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'Les deux mots de passe ne sont pas identiques.',

    // `path` rattache l'erreur au SECOND champ : c'est celui que l'utilisateur doit
    // corriger, et c'est là que le message doit s'afficher.
    path: ['confirmPassword'],
  })
  // Re-passe TOUTES les règles avec l'email réel : la règle « ne contient pas votre adresse
  // email » ne peut être évaluée qu'ici, où les deux champs sont connus.
  .superRefine((values, ctx) => {
    const failed = firstUnsatisfiedRule(values.password, values.email);

    if (failed !== null) {
      ctx.addIssue({
        code: 'custom',
        message: failed.error(values.password, values.email),

        // `path` rattache l'erreur au champ mot de passe : c'est celui à corriger.
        path: ['password'],
      });
    }
  });

/** Identité de la personne : partagée par l'inscription et par Paramètres › Profil. */
const identityShape = {
  firstName,
  lastName,
  jobTitle: z.string().min(1, 'Le poste occupé est obligatoire.').max(120),

  // Rempli seulement quand « Autre » est choisi dans la liste. C'est CE libellé qui part
  // alors au serveur : la valeur « Autre » n'apprend rien à personne.
  customJobTitle: z.string().max(120).optional(),
};

/** Coordonnées du compte : raison sociale, téléphone, site, ville, pays. */
const companyShape = {
  companyName,
  dialCode: z.string().min(1),
  phoneNumber: z
    .string()
    .min(1, 'Le numéro de téléphone est obligatoire.')
    // Les séparateurs saisis (espaces, points, tirets) sont tolérés : refuser
    // « 04 22 11 22 11 » parce qu'il contient des espaces est une brimade, pas une
    // validation. Seuls les chiffres sont comptés.
    .refine((value) => value.replace(/\D/g, '').length >= 6, 'Le numéro de téléphone est incomplet.'),

  websiteUrl: z.string().max(255).optional(),
  city: z.string().max(120).optional(),
  countryCode: z.string().length(2),
};

function hasCustomJobTitleIfNeeded(values: { jobTitle: string; customJobTitle?: string | undefined }) {
  return values.jobTitle !== OTHER_JOB_TITLE || (values.customJobTitle ?? '').trim().length > 0;
}

const customJobTitleIssue = { message: 'Précisez l’intitulé de votre poste.', path: ['customJobTitle'] };

/** Inscription, seconde étape : qui vous êtes et pour quel établissement. */
export const registerProfileSchema = z
  .object({ ...identityShape, ...companyShape })
  .refine(hasCustomJobTitleIfNeeded, customJobTitleIssue);

/**
 * Paramètres › Profil d'un membre qui n'est pas propriétaire : les coordonnées du compte
 * sont affichées en lecture seule et ne sont pas envoyées, elles ne sont donc pas validées.
 * Les valider bloquerait sinon l'enregistrement de son propre nom sur un champ qu'il ne
 * peut pas corriger (un compte ouvert via LinkedIn n'a par exemple aucun téléphone).
 */
export const profileIdentitySchema = z
  .object({
    ...identityShape,
    companyName: z.string(),
    dialCode: z.string(),
    phoneNumber: z.string(),
    websiteUrl: z.string().optional(),
    city: z.string().optional(),
    countryCode: z.string(),
  })
  .refine(hasCustomJobTitleIfNeeded, customJobTitleIssue);

/** Charge utile réellement envoyée à `POST /api/v1/auth/register`. */
export const registerSchema = z.object({
  companyName,
  firstName,
  lastName,
  email,
  password,
});

export const passwordResetRequestSchema = z.object({ email });

/** Code de confirmation reçu par email, tel que saisi dans les cases de l'écran dédié. */
export const confirmEmailSchema = z.object({
  code: z
    .string()
    .length(CONFIRMATION_CODE_LENGTH, `Le code comporte ${CONFIRMATION_CODE_LENGTH} chiffres.`)
    .regex(/^\d+$/, 'Le code ne contient que des chiffres.'),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type RegisterCredentialsInput = z.infer<typeof registerCredentialsSchema>;
export type RegisterProfileInput = z.infer<typeof registerProfileSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;
export type ConfirmEmailInput = z.infer<typeof confirmEmailSchema>;
